import { useState } from "react";
import { MapPinned, Pencil } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatCnpj, formatDateHoraBr } from "@/lib/format";
import { useMunicipios } from "@/lib/municipios";
import { useCnpjsLocalizados, useCnpjsSemLocalizacao, type OrigemLocalizacao } from "./api";
import { formatNumero } from "./cidades";
import { DialogLocalizacao, type AlvoLocalizacao } from "./DialogLocalizacao";
import { ConsultaEstabelecimento } from "./ConsultaAnvisa";
import { SITUACOES_ANVISA } from "./schema";

function chaveLinha({ cnpj, cnes }: { cnpj?: string; cnes?: string }): string {
  return cnpj ?? `cnes-${cnes}`;
}

/** CNPJ formatado ou, em estabelecimento público sem CNPJ, o CNES com selo "público". */
function Identificacao({ cnpj, cnes }: { cnpj?: string; cnes?: string }) {
  if (cnpj) return <>{formatCnpj(cnpj)}</>;
  return (
    <>
      CNES {cnes}
      <Badge variant="outline" className="ml-2">
        público
      </Badge>
    </>
  );
}

const TENTATIVAS_AUTOMATICAS = 3;

const ROTULO_ORIGEM: Record<OrigemLocalizacao, string> = {
  MANUAL: "Manual",
  CEP_CADASTRO: "CEP do cadastro",
  AUTOMATICA_RF: "Automática (Receita)",
  AUTOMATICA_CNES: "Automática (CNES)",
};

/**
 * Onde o mapa perde informação: CNPJs que dispensaram (ou farmácias cadastradas)
 * sem município resolvido. A consulta automática (BrasilAPI/Receita, no
 * dispensacao-microservice) roda primeiro; o que sobra é resolvido aqui à mão,
 * conferindo na Anvisa. A aba "Localizados" permite corrigir o automático.
 *
 * Estabelecimento público só com CNES (sem CNPJ) passa pela consulta automática do CNES
 * (Ministério da Saúde); o que sobra é conferido aqui na consulta do CNES (DATASUS).
 */
export function FilaLocalizacao() {
  const semLocalizacao = useCnpjsSemLocalizacao();
  const localizados = useCnpjsLocalizados();
  const municipios = useMunicipios();
  const [alvo, setAlvo] = useState<AlvoLocalizacao | null>(null);

  return (
    <section className="rounded-xl border border-border bg-card p-5 shadow-card">
      <div className="mb-4 flex items-center gap-2">
        <MapPinned className="h-5 w-5 text-primary" aria-hidden />
        <h2 className="text-base font-semibold text-foreground">Localização de CNPJs</h2>
      </div>

      <Tabs defaultValue="sem">
        <TabsList>
          <TabsTrigger value="sem">Sem localização ({semLocalizacao.data?.length ?? "…"})</TabsTrigger>
          <TabsTrigger value="localizados">Localizados ({localizados.data?.length ?? "…"})</TabsTrigger>
        </TabsList>

        <TabsContent value="sem">
          {semLocalizacao.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>CNPJ</TableHead>
                  <TableHead>Razão social</TableHead>
                  <TableHead>Responsável técnico</TableHead>
                  <TableHead className="text-right">Dispensações</TableHead>
                  <TableHead>Última</TableHead>
                  <TableHead>Consulta automática</TableHead>
                  <TableHead className="w-48" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(semLocalizacao.data ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground">
                      Todos os CNPJs estão localizados.
                    </TableCell>
                  </TableRow>
                ) : (
                  semLocalizacao.data!.map((linha) => (
                    <TableRow key={chaveLinha(linha)}>
                      <TableCell className="whitespace-nowrap font-mono text-xs">
                        <Identificacao cnpj={linha.cnpj} cnes={linha.cnes} />
                        {linha.cadastrada && (
                          <Badge variant="secondary" className="ml-2">
                            cadastrada
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {linha.razaoSocial ?? <span className="text-muted-foreground">—</span>}
                        {linha.situacaoCadastralRf && (
                          <span className="block text-xs text-muted-foreground">Receita: {linha.situacaoCadastralRf}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {linha.nomeResponsavelTecnico ?? "—"}
                        {linha.crfResponsavelTecnico && (
                          <span className="block text-xs text-muted-foreground">
                            CRF {linha.crfResponsavelTecnico}/{linha.ufCrfResponsavelTecnico}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{formatNumero(linha.dispensacoes)}</TableCell>
                      <TableCell className="whitespace-nowrap">{formatDateHoraBr(linha.ultimaDispensacao)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {linha.cadastrada
                          ? "Pelo CEP do cadastro (de hora em hora)"
                          : linha.tentativasAutomaticas >= TENTATIVAS_AUTOMATICAS
                            ? "Esgotada"
                            : `${linha.tentativasAutomaticas} de ${TENTATIVAS_AUTOMATICAS} tentativas`}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <ConsultaEstabelecimento cnpj={linha.cnpj} cnes={linha.cnes} />
                          <Button
                            size="sm"
                            onClick={() =>
                              setAlvo({
                                cnpj: linha.cnpj,
                                cnes: linha.cnes,
                                razaoSocial: linha.razaoSocial,
                                ufSugerida: linha.ufCrfResponsavelTecnico,
                              })
                            }
                          >
                            Registrar
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </TabsContent>

        <TabsContent value="localizados">
          {localizados.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>CNPJ</TableHead>
                  <TableHead>Razão social</TableHead>
                  <TableHead>Município</TableHead>
                  <TableHead>Origem</TableHead>
                  <TableHead>Anvisa</TableHead>
                  <TableHead className="text-right">Dispensações</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(localizados.data ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground">
                      Nenhum CNPJ localizado ainda.
                    </TableCell>
                  </TableRow>
                ) : (
                  localizados.data!.map((linha) => {
                    const municipio = municipios.data?.porCodigo.get(linha.codigoIbge);
                    return (
                      <TableRow key={chaveLinha(linha)}>
                        <TableCell className="whitespace-nowrap font-mono text-xs">
                          <Identificacao cnpj={linha.cnpj} cnes={linha.cnes} />
                        </TableCell>
                        <TableCell>{linha.razaoSocial ?? "—"}</TableCell>
                        <TableCell>{municipio ? `${municipio.nome}/${municipio.uf}` : linha.codigoIbge}</TableCell>
                        <TableCell>
                          <Badge variant={linha.origem === "MANUAL" ? "default" : "secondary"}>
                            {ROTULO_ORIGEM[linha.origem]}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">
                          {SITUACOES_ANVISA.find((s) => s.value === linha.situacaoAnvisa)?.label ?? "—"}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{formatNumero(linha.dispensacoes)}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Corrigir localização de ${linha.cnpj ? formatCnpj(linha.cnpj) : `CNES ${linha.cnes}`}`}
                            onClick={() =>
                              setAlvo({
                                cnpj: linha.cnpj,
                                cnes: linha.cnes,
                                codigoIbge: linha.codigoIbge,
                                razaoSocial: linha.razaoSocial,
                                situacaoAnvisa: linha.situacaoAnvisa,
                                observacao: linha.observacao,
                              })
                            }
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </TabsContent>
      </Tabs>

      <DialogLocalizacao alvo={alvo} onClose={() => setAlvo(null)} />
    </section>
  );
}
